import {useState} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {KairosMark} from '@/components/kairos-mark'
import {NoAccessState} from '@/components/no-access-state'
import {DeleteBusinessDialog} from '@/components/delete-business-dialog'
import {EmpresaFacturas, EmpresaIntentosCargo, PlanYSuscripcion} from '@/components/cartera/empresa-panel-cobro'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {SectionCard, StatCard} from '@/components/section-card'
import {BateriaTab} from '@/components/bateria/bateria-tab'
import {useBusiness, useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {
  NOTES_PAGE_SIZE,
  useAddNote,
  useBusinessDetail,
  useBusinessNotes,
  type AttentionSignal,
} from '@/hooks/inci/consola/use-admin'
import {useSgsst} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useBillingState} from '@/hooks/inci/cartera/use-billing'
import {api} from '@/lib/api'
import {signalRow, signalTone} from '@/lib/attention'
import {BAND_TONE} from '@/lib/bands'
import {RISK_CLASS_ROMAN, RISK_CLASS_TONE, formatRatePct} from '@/lib/risk'
import {formatSegmentKey} from '@/lib/segments'

const noteDateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

function Eyebrow({children}: {children: React.ReactNode}) {
  return (
    <p className="mb-2.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
      {children}
    </p>
  )
}

function SignalItem({label, detail, tone}: {label: string; detail?: string; tone: 'red' | 'amber'}) {
  return (
    <div className="flex items-center gap-2.5 border-b border-dashed border-border py-1.5 last:border-b-0">
      <span className={`size-2 shrink-0 rounded-sm ${tone === 'red' ? 'swatch-red' : 'swatch-amber'}`} />
      <span className="flex-1 text-[13px]">{label}</span>
      {detail && <span className="font-mono text-[11.5px] whitespace-nowrap text-muted-foreground">{detail}</span>}
    </div>
  )
}

function CopyButton({value}: {value: string}) {
  const {t} = useTranslation()
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className="inline-flex h-[26px] items-center gap-1.5 rounded-md border border-border bg-background px-2 font-mono text-[11px] tracking-wide text-muted-foreground uppercase">
      {copied ? t('admin.sheet.copied') : t('admin.sheet.copy')}
    </button>
  )
}

function ContactRow({label, value}: {label: string; value: string}) {
  return (
    <div className="grid grid-cols-[74px_1fr_auto] items-center gap-2.5">
      <span className="font-mono text-[10px] font-medium tracking-wide text-muted-foreground uppercase">{label}</span>
      <span className="min-w-0 font-mono text-[13px] break-words">{value}</span>
      <CopyButton value={value} />
    </div>
  )
}

/** Cifras derivadas de datos que el backend ya expone, nunca de un cálculo paralelo. */
function EmpresaResumenVitals({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: adminDetail, isLoading} = useBusinessDetail(businessId)
  const {data: sgsst} = useSgsst(businessId)

  if (isLoading || !adminDetail) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="empresa-panel-resumen-vitals-loading">
        {Array.from({length: 4}).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  const hasAssessment = adminDetail.score_pct != null && adminDetail.band != null
  const signalCount = adminDetail.signals?.length ?? 0
  const cumpleTotal = adminDetail.cumple_total
  const cumpleConSoporte = adminDetail.cumple_con_soporte ?? 0

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="empresa-panel-resumen-vitals">
      <StatCard
        testId="empresa-panel-vital-empleados"
        label={t('cartera.empresaPanel.resumen.empleadosActivos')}
        value={sgsst?.active_employee_count ?? '—'}
      />
      <StatCard
        testId="empresa-panel-vital-autoevaluacion"
        label={t('cartera.empresaPanel.resumen.autoevaluacion')}
        value={
          hasAssessment ? (
            <span className="flex flex-wrap items-center gap-1.5">
              {formatRatePct(adminDetail.score_pct!)}
              <Tag tone={BAND_TONE[adminDetail.band!]}>{t(`sgsst.bands.${adminDetail.band}.label`)}</Tag>
            </span>
          ) : (
            <span className="text-sm text-muted-foreground normal-case">{t('admin.worklist.startup')}</span>
          )
        }
        pct={hasAssessment ? adminDetail.score_pct! : undefined}
      />
      <StatCard
        testId="empresa-panel-vital-soportes"
        label={t('cartera.empresaPanel.resumen.soportes')}
        value={cumpleTotal != null ? `${cumpleConSoporte}/${cumpleTotal}` : '—'}
        pct={cumpleTotal ? (cumpleConSoporte / cumpleTotal) * 100 : undefined}
      />
      <StatCard
        testId="empresa-panel-vital-senales"
        label={t('cartera.empresaPanel.resumen.senales')}
        value={signalCount}
        alert={signalCount > 0}
      />
    </div>
  )
}

function EmpresaResumenSenales({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: adminDetail} = useBusinessDetail(businessId)

  if (!adminDetail) return <Skeleton className="h-16 w-full" />

  const signals: AttentionSignal[] = adminDetail.signals ?? []
  const cumpleGap =
    adminDetail.cumple_total != null ? adminDetail.cumple_total - (adminDetail.cumple_con_soporte ?? 0) : 0

  if (signals.length === 0 && cumpleGap === 0) return null

  return (
    <SectionCard title={t('admin.sheet.signals')} bodyClassName="px-5 py-4" testId="empresa-panel-resumen-senales">
      {signals.map((signal) => {
        const {label, detail} = signalRow(t, signal)
        return (
          <SignalItem key={signal.kind} label={label} detail={detail || undefined} tone={signalTone(signal.bucket)} />
        )
      })}
      {cumpleGap > 0 && (
        <SignalItem
          label={t('admin.sheet.cumpleGap', {count: cumpleGap})}
          detail={t('admin.sheet.cumpleGapDetail', {total: adminDetail.cumple_total ?? 0})}
          tone="amber"
        />
      )}
    </SectionCard>
  )
}

export function EmpresaContactoYBitacora({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const [notesPage, setNotesPage] = useState(1)
  const {data: adminDetail} = useBusinessDetail(businessId)
  const {data: notes} = useBusinessNotes(businessId, notesPage)
  const addNote = useAddNote(businessId)
  const [text, setText] = useState('')

  if (!adminDetail) return <Skeleton className="h-24 w-full" />

  const contact = adminDetail.contact

  function submitNote() {
    const trimmed = text.trim()
    if (!trimmed) return
    // Vuelve a la página 1: la bitácora es descendente y la nota nueva vive ahí.
    addNote.mutate(trimmed, {
      onSuccess: () => {
        setText('')
        setNotesPage(1)
      },
    })
  }

  return (
    <SectionCard
      title={t('admin.sheet.contact')}
      bodyClassName="space-y-4 px-5 py-4"
      testId="empresa-panel-contacto-bitacora">
      {contact && (
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span className="min-w-0 break-words">{contact.legal_rep_name}</span>
            <Tag tone="neutral" className="shrink-0">
              {t('admin.sheet.legalRep')}
            </Tag>
          </div>
          <div className="mt-2.5 flex flex-col gap-2">
            {contact.legal_rep_phone && <ContactRow label={t('admin.sheet.phone')} value={contact.legal_rep_phone} />}
            {contact.legal_rep_email && <ContactRow label={t('admin.sheet.email')} value={contact.legal_rep_email} />}
            {!contact.legal_rep_phone && !contact.legal_rep_email && (
              <p className="text-[13px] text-muted-foreground">{t('admin.sheet.noContact')}</p>
            )}
          </div>
        </div>
      )}

      <div className={contact ? 'surface-divider border-t pt-4' : ''}>
        <Eyebrow>{t('admin.sheet.bitacora')}</Eyebrow>
        <div className="flex flex-col gap-3.5">
          {notes?.items.map((note) => (
            <div key={note.id} className="relative pl-4">
              <span className="absolute top-1.5 left-0 size-1.5 rounded-full border-[1.5px] border-primary bg-accent" />
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 truncate text-[13px] font-semibold">{note.author_name}</span>
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                  {noteDateFormat.format(new Date(note.created_at))}
                </span>
              </div>
              <p className="mt-0.5 text-[13px] break-words text-foreground">{note.text}</p>
            </div>
          ))}
          {notes && notes.items.length === 0 && (
            <p className="text-[13px] text-muted-foreground">{t('admin.sheet.bitacoraEmpty')}</p>
          )}
        </div>
        {!!notes?.total && (
          <PaginationBar page={notesPage} pageSize={NOTES_PAGE_SIZE} total={notes.total} onPageChange={setNotesPage} />
        )}
        <div className="mt-3.5 space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('admin.sheet.bitacoraPlaceholder')}
            maxLength={2000}
            minRows={2}
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={submitNote} disabled={addNote.isPending || !text.trim()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  )
}

function EmpresaHeader({
  businessId,
  businessName,
  nit,
  segmentKey,
  partnerId,
  riskClass,
  onDelete,
}: {
  businessId: string
  businessName: string
  nit: string
  segmentKey: string | null
  partnerId: string | null
  riskClass: {risk_class: number; initial_rate_pct: number} | null
  onDelete: () => void
}) {
  const {t} = useTranslation()
  const navigate = useNavigate()

  return (
    // «Entrar» va en contorno: el único primario de la vista lo pide la pestaña activa (U7).
    <div className="surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-foreground">
            <KairosMark />
            <h1 className="font-heading text-xl">{businessName}</h1>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{nit}</span>
            {segmentKey && <Tag tone="neutral">{formatSegmentKey(segmentKey, t)}</Tag>}
            {riskClass && (
              <>
                <Tag tone={RISK_CLASS_TONE[riskClass.risk_class]}>
                  {t('business.risk.classValue', {roman: RISK_CLASS_ROMAN[riskClass.risk_class]})}
                </Tag>
                <span className="font-mono text-xs text-muted-foreground">
                  {formatRatePct(riskClass.initial_rate_pct)}
                </span>
              </>
            )}
            {partnerId && (
              <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs">
                <a href="/admin/partners" data-testid="empresa-panel-partner-link">
                  {t('cartera.empresaPanel.partnerLink')}
                </a>
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="empresa-panel-enter"
            onClick={() => navigate(`/businesses/${businessId}`)}>
            {t('admin.sheet.enter')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            data-testid="empresa-panel-eliminar"
            onClick={onDelete}>
            {t('business.delete')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function EmpresaPanelPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {businessId} = useParams()
  const isSystemUser = useIsSystemUser()
  const {data: business, isLoading: businessLoading} = useBusiness(businessId)
  const {data: sgsst} = useSgsst(businessId)
  const {data: billingState} = useBillingState(businessId)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const deleteBusiness = useMutation({
    mutationFn: async () => api.delete(`/businesses/${businessId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['businesses']})
      queryClient.invalidateQueries({queryKey: ['admin-stats']})
      queryClient.invalidateQueries({queryKey: ['admin-attention']})
      navigate('/admin/empresas')
    },
  })

  if (!isSystemUser) return <NoAccessState />
  if (businessLoading) return <Skeleton className="h-40 w-full" />
  if (!business || !businessId) return <NoAccessState />

  const segmentKey = billingState?.segment_key ?? null
  const requireTypedName = (sgsst?.active_employee_count ?? 0) > 0 || sgsst?.assessment != null

  return (
    <div className="space-y-6" data-testid="empresa-panel-page">
      <Link
        to="/admin/empresas"
        className="text-[13px] font-medium text-muted-foreground hover:text-foreground"
        data-testid="empresa-panel-back">
        {t('business.backToBusinesses')}
      </Link>
      <EmpresaHeader
        businessId={businessId}
        businessName={business.name}
        nit={business.nit}
        segmentKey={segmentKey}
        partnerId={business.partner_id}
        riskClass={business.risk_classification ?? null}
        onDelete={() => setConfirmingDelete(true)}
      />

      <Tabs defaultValue="resumen" data-testid="empresa-panel-tabs">
        <TabsList>
          <TabsTrigger value="resumen" data-testid="empresa-panel-tab-resumen">
            {t('cartera.empresaPanel.tabs.resumen')}
          </TabsTrigger>
          <TabsTrigger value="contacto" data-testid="empresa-panel-tab-contacto">
            {t('cartera.empresaPanel.tabs.contacto')}
          </TabsTrigger>
          <TabsTrigger value="plan" data-testid="empresa-panel-tab-plan">
            {t('cartera.empresaPanel.tabs.plan')}
          </TabsTrigger>
          <TabsTrigger value="facturas" data-testid="empresa-panel-tab-facturas">
            {t('cartera.empresaPanel.tabs.facturas')}
          </TabsTrigger>
          <TabsTrigger value="cobros" data-testid="empresa-panel-tab-cobros">
            {t('cartera.empresaPanel.tabs.cobros')}
          </TabsTrigger>
          {/* Nueva evaluación vive aquí porque /admin/bateria es un worklist cross-empresa sin selector. */}
          <TabsTrigger value="bateria" data-testid="empresa-panel-tab-bateria">
            {t('cartera.empresaPanel.tabs.bateria')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="space-y-4 pt-4">
          <EmpresaResumenVitals businessId={businessId} />
          <EmpresaResumenSenales businessId={businessId} />
        </TabsContent>

        <TabsContent value="contacto" className="pt-4">
          <EmpresaContactoYBitacora businessId={businessId} />
        </TabsContent>

        <TabsContent value="plan" className="pt-4">
          <PlanYSuscripcion businessId={businessId} segmentKey={segmentKey} planTier={business.plan_tier} />
        </TabsContent>

        <TabsContent value="facturas" className="pt-4">
          <EmpresaFacturas businessId={businessId} />
        </TabsContent>

        <TabsContent value="cobros" className="pt-4">
          <EmpresaIntentosCargo businessId={businessId} />
        </TabsContent>

        <TabsContent value="bateria" className="pt-4">
          <BateriaTab businessId={businessId} />
        </TabsContent>
      </Tabs>

      <DeleteBusinessDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        businessName={business.name}
        requireTypedName={requireTypedName}
        pending={deleteBusiness.isPending}
        onConfirm={() => deleteBusiness.mutate()}
      />
    </div>
  )
}
